import { useState, useEffect, useCallback } from "react";
import { Select } from "../../common";
import { graphBuilderClient } from "../../../services/api";

type SynapsePreset = {
  id: string;
  name: string;
  description: string;
  weight: number;
  delayMs: number;
};

type SynapsePresetSelectorProps = {
  edgeName: string;
  currentWeight?: number;
  onPresetApply: (preset: { weight: number; delayMs: number; presetId: string }) => void;
};

const SynapsePresetSelector = ({
  edgeName,
  currentWeight,
  onPresetApply,
}: SynapsePresetSelectorProps) => {
  const [presets, setPresets] = useState<SynapsePreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("_custom");
  const [weight, setWeight] = useState<number>(currentWeight ?? 0.5);
  const [loading, setLoading] = useState(false);

  const fetchPresets = useCallback(async () => {
    if (!edgeName) return;
    setLoading(true);
    try {
      const res = await graphBuilderClient.get(`/api/presets/edge?edge_name=${encodeURIComponent(edgeName)}`);
      const data = res?.data || res;
      setPresets(data.presets || []);
      if (data.presets?.length > 0 && selectedPresetId === "_custom") {
        const match = data.presets.find(
          (p: SynapsePreset) => Math.abs(p.weight - (currentWeight ?? 0.5)) < 0.05
        );
        if (match) setSelectedPresetId(match.id);
      }
    } catch {
      setPresets([]);
    } finally {
      setLoading(false);
    }
  }, [edgeName]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  useEffect(() => {
    setWeight(currentWeight ?? 0.5);
  }, [currentWeight]);

  const handlePresetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const presetId = e.target.value;
    setSelectedPresetId(presetId);
    if (presetId === "_custom") return;
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      setWeight(preset.weight);
      onPresetApply({ weight: preset.weight, delayMs: preset.delayMs, presetId: preset.id });
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setWeight(val);
    setSelectedPresetId("_custom");
    onPresetApply({ weight: val, delayMs: 0, presetId: "_custom" });
  };

  if (!edgeName || loading) return null;
  if (presets.length === 0) return null;

  const presetOptions = [
    { id: "_custom", value: "_custom", label: "Custom" },
    ...presets.map((p) => ({
      id: p.id,
      value: p.id,
      label: `${p.name} — ${p.description}`,
    })),
  ];

  return (
    <div className="neural-preset-selector synapse-preset-selector">
      <div className="neural-preset-selector__header">
        <p className="node-ratecoding-section__eyebrow">Connection Behavior</p>
        <p className="node-ratecoding-section__description">
          Select a strength preset or fine-tune with the slider.
        </p>
      </div>
      <div className="synapse-entity-modal__form-group">
        <Select
          id="synapse-preset"
          name="synapsePreset"
          value={selectedPresetId}
          onChange={handlePresetChange}
          variant="modal"
          options={presetOptions}
          label="Strength Preset"
        />
      </div>
      <div className="synapse-entity-modal__form-group neural-preset-selector__slider">
        <label htmlFor="weight-slider" className="neural-preset-selector__label">
          Connection Strength: <strong>{weight.toFixed(2)}</strong>
          <span className="neural-preset-selector__hint">
            {weight < 0.3 ? " (weak)" : weight < 0.6 ? " (moderate)" : weight < 0.8 ? " (strong)" : " (dominant)"}
          </span>
        </label>
        <input
          id="weight-slider"
          type="range"
          min="0.05"
          max="1.0"
          step="0.05"
          value={weight}
          onChange={handleWeightChange}
          className="neural-preset-selector__range"
        />
        <div className="neural-preset-selector__range-labels">
          <span>Weak</span>
          <span>Dominant</span>
        </div>
      </div>
    </div>
  );
};

export default SynapsePresetSelector;
